import React from 'react';
import { Box, Chip, ListItemText, Menu, MenuItem, Skeleton, Stack, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import type { FunnelOverlaySummary, StatTier } from '../types';
import type { TraffickedVersion } from '../hooks/useTraffickedVersions';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { STAT_TIER_COPY } from '../statTierCopy';
import { PEOPLE_ABSENCE_LABEL, useFunnelUnit } from '../funnelUnit';

/**
 * Spec D D1b (§12.1 C1): the honesty banner above the analytics canvas —
 * entrants, window and a server-computed sufficiency verdict. Render-only:
 * the tier arrives in the overlay `summary` (AC-D1-7 discipline).
 *
 * Reports parity follow-up: the banner also states WHICH period the numbers
 * cover (the range carried over from the Reports dashboard Open action, or
 * «All time») and the dashboard-parity Starts line («N starts · M people ·
 * this version») so the canvas and the placement row read like-for-like.
 */

const SUFFICIENCY_COPY: Record<StatTier, string> = {
  solid: 'sample is sufficient for per-screen reads.',
  directional: 'directions are readable, sizes are not. Chips on nodes tell you which numbers to trust.',
  insufficient: 'too early to read per-screen numbers — treat everything below as a sketch.',
};

const SUFFICIENCY_COLOR: Record<StatTier, 'success.main' | 'warning.main' | 'text.disabled'> = {
  solid: 'success.main',
  directional: 'warning.main',
  insufficient: 'text.disabled',
};

const DAY_MS = 86_400_000;

const formatUtcDay = (ms: number): string =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
    .format(new Date(ms));

/**
 * Reports sends UTC inclusive day ranges as `[day, nextDay)` — display the
 * inclusive end so the chip matches the dashboard calendar the user selected.
 */
export function formatOverlayWindowLabel(from?: string, to?: string): string {
  if (!from || !to) return 'All time';
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return 'All time';
  const inclusiveEndMs = toMs % DAY_MS === 0 ? toMs - DAY_MS : toMs;
  const fromLabel = formatUtcDay(fromMs);
  const endLabel = formatUtcDay(Math.max(fromMs, inclusiveEndMs));
  return fromLabel === endLabel ? `${fromLabel} (UTC)` : `${fromLabel} – ${endLabel} (UTC)`;
}

/**
 * I4 (analytics-versions-and-publish-flow): version chip + dropdown on the
 * banner. Lists ONLY versions with traffic in the window (starts each); the
 * current version is appended marked «no traffic in window» when it is not
 * among them. Switching goes through the editor's existing switchVersion.
 */
export interface HonestyBannerVersionSwitcher {
  currentVersionId: string;
  currentVersionName?: string;
  versions: TraffickedVersion[];
  onSwitch: (versionId: string) => void;
}

interface AnalyticsHonestyBannerProps {
  summary?: FunnelOverlaySummary;
  loading?: boolean;
  /** Report window the overlay was queried with (absent = all-time). */
  windowFrom?: string;
  windowTo?: string;
  /** Absent = no switcher (list unavailable or a single lonely version). */
  versionSwitcher?: HonestyBannerVersionSwitcher;
  /**
   * I-B2 (P-9, additive): applied-filter chips — chip ✕ removes the clause
   * and re-applies immediately (RC-AC-027). Absent/empty = row not rendered.
   */
  filterChips?: Array<{ id: string; label: string; onRemove: () => void }>;
  /**
   * Pre-answer zone note (trait filters): says WHERE the filtered numbers
   * start being observable. Absent = no zone / no trait filter.
   */
  filterZoneNote?: string;
}

const shortVersionId = (versionId: string): string =>
  versionId.length > 8 ? `${versionId.slice(0, 8)}…` : versionId;

function VersionSwitcherChip({ switcher }: { switcher: HonestyBannerVersionSwitcher }): React.ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const currentLabel = switcher.currentVersionName || shortVersionId(switcher.currentVersionId);
  const currentTrafficked = switcher.versions.find(version => version.versionId === switcher.currentVersionId);
  return (
    <>
      <Chip
        data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerVersionChip}
        label={`Version: ${currentLabel}${currentTrafficked ? ` · ${currentTrafficked.starts.toLocaleString('en-US')} starts` : ''}`}
        size="small"
        variant="outlined"
        onClick={event => setAnchorEl(event.currentTarget)}
        onDelete={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        deleteIcon={<ArrowDropDownIcon />}
        sx={{ fontWeight: 600 }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {switcher.versions.map(version => (
          <MenuItem
            key={version.versionId}
            data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerVersionItem(version.versionId)}
            selected={version.versionId === switcher.currentVersionId}
            onClick={() => {
              setAnchorEl(null);
              if (version.versionId !== switcher.currentVersionId) switcher.onSwitch(version.versionId);
            }}
          >
            <ListItemText
              primary={version.versionName || shortVersionId(version.versionId)}
              secondary={`${version.starts.toLocaleString('en-US')} starts`}
            />
          </MenuItem>
        ))}
        {!currentTrafficked && (
          <MenuItem
            key={switcher.currentVersionId}
            data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerVersionItem(switcher.currentVersionId)}
            selected
            onClick={() => setAnchorEl(null)}
          >
            <ListItemText primary={currentLabel} secondary="no traffic in window" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export const AnalyticsHonestyBanner: React.FC<AnalyticsHonestyBannerProps> = ({
  summary,
  loading = false,
  windowFrom,
  windowTo,
  versionSwitcher,
  filterChips,
  filterZoneNote,
}) => {
  // F2 (FT-AC-083): in people mode the banner LEADS with the distinct-people
  // figure; starts stays visible as context labelled «(visits)». The summary
  // people figure (`uniqueVisitors`) is the dashboard-parity Starts twin.
  const unit = useFunnelUnit();
  if (loading && !summary) {
    return (
      <Box sx={{ px: 2, py: 1 }} data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBanner} data-sufficiency="loading">
        <Skeleton variant="text" width={360} />
      </Box>
    );
  }
  if (!summary) return null;

  const tier = summary.sufficiency;
  const windowLabel = formatOverlayWindowLabel(windowFrom, windowTo);
  const hasStarts = typeof summary.startedJourneys === 'number';
  return (
    <Box
      data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBanner}
      data-sufficiency={tier}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: 'background.paper',
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        borderLeft: theme => `4px solid ${
          tier === 'solid' ? theme.palette.success.main
            : tier === 'directional' ? theme.palette.warning.main
              : theme.palette.divider
        }`,
      }}
    >
      <Typography component="span" sx={{ color: SUFFICIENCY_COLOR[tier], fontWeight: 700 }}>
        {STAT_TIER_COPY[tier].icon}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        <Chip
          data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerWindow}
          label={windowLabel}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
        {versionSwitcher && <VersionSwitcherChip switcher={versionSwitcher} />}
        {hasStarts && (
          <Typography variant="body2" data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerStarts}>
            {unit === 'people' ? (
              typeof summary.uniqueVisitors === 'number' ? (
                <>
                  <strong>{summary.uniqueVisitors.toLocaleString('en-US')} people</strong>
                  {` · ${(summary.startedJourneys as number).toLocaleString('en-US')} starts (visits)`}
                </>
              ) : (
                // D7: people twin absent on this payload — visits stay, absence said.
                <>
                  <strong>{(summary.startedJourneys as number).toLocaleString('en-US')} starts</strong>
                  {` · ${PEOPLE_ABSENCE_LABEL}`}
                </>
              )
            ) : (
              <>
                <strong>{(summary.startedJourneys as number).toLocaleString('en-US')} starts</strong>
                {typeof summary.uniqueVisitors === 'number' && (
                  <> · {summary.uniqueVisitors.toLocaleString('en-US')} people</>
                )}
              </>
            )}
            {' · this version ·'}
          </Typography>
        )}
        <Typography variant="body2">
          {/* Entrants are journey-grain; in people mode the unit is named
              rather than the figure silently reused (FT-AC-085). */}
          <strong>{summary.entrants.toLocaleString('en-US')} entrants{unit === 'people' ? ' (visits)' : ''}</strong>
          {summary.windowDays !== undefined && (
            <> over the last <strong>{summary.windowDays} day{summary.windowDays === 1 ? '' : 's'}</strong></>
          )}
          {' — '}{SUFFICIENCY_COPY[tier]}
        </Typography>
        {/* I-B2 (P-9): purely additive applied-filter chips — absent when no filters. */}
        {filterChips && filterChips.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerFilterChips}
          >
            {filterChips.map((chip, index) => (
              <Chip
                key={chip.id}
                data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerFilterChip(index)}
                label={chip.label}
                size="small"
                variant="outlined"
                onDelete={chip.onRemove}
              />
            ))}
          </Stack>
        )}
        {filterZoneNote && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
            data-testid={ANALYTICS_CANVAS_TEST_IDS.honestyBannerFilterZoneNote}
          >
            {filterZoneNote}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
